import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';
import { ViewIcon, EditIcon, DeleteIcon } from '../components/IconButtons';
import AdminSidebar from '../components/AdminSidebar';
import * as XLSX from 'xlsx';

const AdminDaftarApresiasiPage = () => {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedApresiasi, setSelectedApresiasi] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: true // Open by default since we're in a master data page
  });
  const [user, setUser] = useState(null);

  // Apresiasi data from API
  const [apresiasis, setApresiasis] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchApresiasi = async () => {
      setLoading(true);
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
        let apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/apresiasis?page=${currentPage}`;
        if (searchQuery && searchQuery.trim() !== '') {
          apiUrl += `&search=${encodeURIComponent(searchQuery)}`;
        }
        const response = await authFetch(apiUrl);
        if (!response.ok) throw new Error('Failed to fetch apresiasi');
        const data = await response.json();
        setApresiasis(data.data);
        setLastPage(data.last_page);
        setTotal(data.total);
      } catch (err) {
        setNotification({ show: true, message: 'Gagal memuat data apresiasi: ' + err.message, type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchApresiasi();
  }, [currentPage, searchQuery]);

  const [formData, setFormData] = useState({
    description: '',
    point: '',
  });

  // No need to filter apresiasis on the frontend anymore, backend search is used.
  const filteredApresiasis = apresiasis;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddApresiasi = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    formDataToSend.append('description', formData.description);
    formDataToSend.append('point', formData.point);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/apresiasis`;

      const token = localStorage.getItem('token');
      const response = await authFetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          // ...other headers as needed
        },
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        console.error('API error:', errorData);

        // Handle validation errors specifically
        if (response.status === 422 && errorData.errors) {
          // Format validation errors nicely
          const errorMessages = [];
          for (const field in errorData.errors) {
            errorMessages.push(errorData.errors[field][0]);
          }
          setNotification({
            show: true,
            message: 'Validasi gagal: ' + errorMessages.join(', '),
            type: 'error'
          });
        } else {
          setNotification({
            show: true,
            message: 'Gagal menambah apresiasi: ' + (errorData.message || JSON.stringify(errorData)),
            type: 'error'
          });
        }
        return;
      }

      const newApresiasi = await response.json();

      // Show success notification
      setNotification({
        show: true,
        message: 'Apresiasi berhasil ditambahkan!',
        type: 'success'
      });

      setApresiasis(prev => [...prev, newApresiasi]);
      setFormData({
        description: '',
        point: ''
      });
      setShowAddModal(false);
    } catch (err) {
      console.error('Network error:', err);
      setNotification({
        show: true,
        message: 'Terjadi kesalahan jaringan: ' + err.message,
        type: 'error'
      });
    }
  };

  const handleEditApresiasi = async (e) => {
    e.preventDefault();
    if (!selectedApresiasi) return;
    const formDataToSend = new FormData();
    formDataToSend.append('description', formData.description);
    formDataToSend.append('point', formData.point);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/apresiasis/${selectedApresiasi.id}`;
      const token = localStorage.getItem('token');
      const response = await authFetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          // ...other headers as needed
        },
        method: 'POST',
        headers: {
          'X-HTTP-Method-Override': 'PATCH'
        },
        body: formDataToSend
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try { errorData = JSON.parse(errorText); } catch (e) { errorData = { message: errorText }; }

        // Handle validation errors specifically
        if (response.status === 422 && errorData.errors) {
          // Format validation errors nicely
          const errorMessages = [];
          for (const field in errorData.errors) {
            errorMessages.push(errorData.errors[field][0]);
          }
          setNotification({
            show: true,
            message: 'Validasi gagal: ' + errorMessages.join(', '),
            type: 'error'
          });
        } else {
          setNotification({ show: true, message: 'Gagal mengedit apresiasi: ' + (errorData.message || JSON.stringify(errorData)), type: 'error' });
        }
        return;
      }
      const updatedApresiasi = await response.json();
      setNotification({ show: true, message: 'Apresiasi berhasil diperbarui!', type: 'success' });
      setApresiasis(prev => prev.map(apresiasi => apresiasi.id === selectedApresiasi.id ? updatedApresiasi : apresiasi));
      setFormData({ description: '', point: '' });
      setShowEditModal(false);
    } catch (err) {
      setNotification({ show: true, message: 'Terjadi kesalahan jaringan: ' + err.message, type: 'error' });
    }
  };

  const handleDeleteApresiasi = async () => {
    if (!selectedApresiasi) return;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
    const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/apresiasis/${selectedApresiasi.id}`;
    try {
      const token = localStorage.getItem('token');
      const response = await authFetch(apiUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        // ...other headers as needed
      }, method: 'DELETE' });
      if (!response.ok) throw new Error('Gagal menghapus apresiasi.');
      setApresiasis(prev => prev.filter(apresiasi => apresiasi.id !== selectedApresiasi.id));
      setNotification({ show: true, message: 'Apresiasi berhasil dihapus!', type: 'success' });
    } catch (err) {
      setNotification({ show: true, message: err.message || 'Gagal menghapus apresiasi.', type: 'error' });
    }
    setShowDeleteModal(false);
  };

  const openEditModal = (apresiasi) => {
    setSelectedApresiasi(apresiasi);
    setFormData({
      description: apresiasi.description || '',
      point: apresiasi.point || ''
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (apresiasi) => {
    setSelectedApresiasi(apresiasi);
    setShowDeleteModal(true);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Map Excel columns to our format
          const mappedData = jsonData.map((row, index) => {
            const description = row['Jenis Tindakan'] || row['jenis_tindakan'] || row['description'] || '';
            const point = row['Poin'] || row['poin'] || row['point'] || '';

            return {
              row: index + 2, // Excel row number (starting from 2, assuming row 1 is header)
              description: description.toString().trim(),
              point: parseInt(point) || 0,
              valid: description.trim() !== '' && !isNaN(parseInt(point))
            };
          }).filter(item => item.description !== ''); // Filter out empty rows

          setImportData(mappedData);
        } catch (error) {
          setNotification({
            show: true,
            message: 'Error membaca file Excel: ' + error.message,
            type: 'error'
          });
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleImportSubmit = async () => {
    if (importData.length === 0) {
      setNotification({
        show: true,
        message: 'Tidak ada data untuk diimpor',
        type: 'error'
      });
      return;
    }

    const validData = importData.filter(item => item.valid);
    if (validData.length === 0) {
      setNotification({
        show: true,
        message: 'Tidak ada data valid untuk diimpor',
        type: 'error'
      });
      return;
    }

    setImportLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/apresiasis/bulk-import`;

      const response = await authFetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          data: validData.map(item => ({
            description: item.description,
            point: item.point
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Import gagal');
      }

      const result = await response.json();
      
      setNotification({
        show: true,
        message: result.message,
        type: 'success'
      });

      // Refresh the apresiasi list
      const fetchApresiasi = async () => {
        try {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
          let apiUrl = `${API_BASE_URL.replace(/\/$/, '')}/api/apresiasis?page=${currentPage}`;
          if (searchQuery && searchQuery.trim() !== '') {
            apiUrl += `&search=${encodeURIComponent(searchQuery)}`;
          }
          const response = await authFetch(apiUrl);
          if (response.ok) {
            const data = await response.json();
            setApresiasis(data.data);
            setLastPage(data.last_page);
            setTotal(data.total);
          }
        } catch (err) {
          console.error('Error refreshing data:', err);
        }
      };
      
      fetchApresiasi();
      setShowImportModal(false);
      setImportFile(null);
      setImportData([]);

    } catch (error) {
      setNotification({
        show: true,
        message: 'Error mengimpor data: ' + error.message,
        type: 'error'
      });
    } finally {
      setImportLoading(false);
    }
  };
  
  // Hide notification after 5 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);
  
  // Get user data for sidebar
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);
  
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : ''; 

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <h1>Manajemen Apresiasi</h1>
          <p>Kelola data apresiasi siswa</p>
        </div>
      </PageHeader>
      
      <DashboardGrid>
        <MainSection>
          <ActionHeader>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Cari apresiasi"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
            <ActionButtonGroup>
              <ImportButton onClick={() => {
                setImportFile(null);
                setImportData([]);
                setShowImportModal(true);
              }}>
                📥 Import Excel
              </ImportButton>
              <AddButton onClick={() => {
                setFormData({
                  description: '',
                  point: ''
                });
                setShowAddModal(true);
              }}>
                + Tambah Apresiasi
              </AddButton>
            </ActionButtonGroup>
          </ActionHeader>
          
          <TableContainer>
            <Table>
            <thead>
              <tr>
                <th>Deskripsi</th>
                <th>Poin</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3}>Memuat data apresiasi...</td></tr>
              ) : apresiasis.length === 0 ? (
                <tr><td colSpan={3}>Tidak ada data apresiasi.</td></tr>
              ) : (
                apresiasis.map(apresiasi => (
                  <tr key={apresiasi.id}>
                    <td style={{ maxWidth: '500px' }}>{apresiasi.description}</td>
                    <td>{apresiasi.point}</td>
                    <td>
                      <Button onClick={() => { setSelectedApresiasi(apresiasi); setShowViewModal(true); }} style={{ backgroundColor: '#2b5da1', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Lihat">
  <ViewIcon />
</Button>
<Button onClick={() => openEditModal(apresiasi)} style={{ backgroundColor: '#2b5da1', marginRight: '0.5rem', padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Edit">
  <EditIcon />
</Button>
<DeleteButton onClick={() => openDeleteModal(apresiasi)} style={{ padding: '0.5rem', width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Hapus">
  <DeleteIcon />
</DeleteButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            </Table>
          </TableContainer>
          {/* Pagination Controls */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} aria-label="Sebelumnya">&lt;</button>
            <span>Halaman {currentPage} dari {lastPage}</span>
            <button onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))} disabled={currentPage === lastPage} aria-label="Berikutnya">&gt;</button>
          </div>
        </MainSection>
        <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
      </DashboardGrid>
      
      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>×</CloseNotificationButton>
        </NotificationToast>
      )}

      {/* View Modal */}
      {showViewModal && selectedApresiasi && (
        <Modal onClick={() => setShowViewModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>Detail Apresiasi</h2>
            <FormGroup>
              <Label>Deskripsi</Label>
              <div>{selectedApresiasi.description}</div>
            </FormGroup>
            <FormGroup>
              <Label>Poin</Label>
              <div>{selectedApresiasi.point}</div>
            </FormGroup>
            <ButtonGroup>
              <CancelButton type="button" onClick={() => setShowViewModal(false)}>
                Tutup
              </CancelButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Add Apresiasi Modal */}
      {showAddModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Tambah Apresiasi Baru</h2>
              <CloseButton onClick={() => setShowAddModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <form onSubmit={handleAddApresiasi}>
  <FormGroup>
    <Label>Deskripsi</Label>
    <Textarea
      name="description"
      value={formData.description}
      onChange={handleInputChange}
      required
    />
  </FormGroup>
  <FormGroup>
    <Label>Poin</Label>
    <Input
      type="number"
      name="point"
      value={formData.point}
      onChange={handleInputChange}
      required
    />
  </FormGroup>
  <ButtonGroup>
    <CancelButton type="button" onClick={() => setShowAddModal(false)}>
      Batal
    </CancelButton>
    <SubmitButton type="submit">
      Simpan
    </SubmitButton>
  </ButtonGroup>
</form>
</ModalContent>
</Modal>
      )}

      {/* Edit Apresiasi Modal */}
      {showEditModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Edit Data Apresiasi</h2>
              <CloseButton onClick={() => setShowEditModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <form onSubmit={handleEditApresiasi}>
              <FormGroup>
                <Label>Deskripsi</Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Poin</Label>
                <Input
                  type="number"
                  name="point"
                  value={formData.point}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowEditModal(false)}>
                  Batal
                </CancelButton>
                <SubmitButton type="submit">
                  Simpan Perubahan
                </SubmitButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Modal>
          <ModalContent>
            <ModalHeader style={{ background: '#ffeaea', borderRadius: '0.5rem 0.5rem 0 0', borderBottom: '2px solid #e74c3c', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '2rem', color: '#e74c3c', marginRight: 12 }}>⚠️</span>
              <h2 style={{ color: '#e74c3c', margin: 0, fontWeight: 700, fontSize: '1.3rem' }}>Hapus Apresiasi</h2>
              <CloseButton onClick={() => setShowDeleteModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <p style={{ color: '#b71c1c', fontWeight: 500, marginTop: 20 }}>
              Apakah Anda yakin ingin menghapus data apresiasi{' '}
              <strong>{selectedApresiasi?.name}</strong>?
            </p>
            <p style={{ color: '#b71c1c', fontSize: '0.95rem', marginBottom: 32 }}>Tindakan ini <strong>TIDAK DAPAT</strong> dibatalkan.</p>
            <ButtonGroup>
              <CancelButton onClick={() => setShowDeleteModal(false)}>
                Batal
              </CancelButton>
              <DeleteButton onClick={handleDeleteApresiasi}>
                Hapus
              </DeleteButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>Import Data Apresiasi dari Excel</h2>
              <CloseButton onClick={() => setShowImportModal(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>Format Excel yang diperlukan:</Label>
              <ImportFormatInfo>
                <p><strong>Kolom yang harus ada:</strong></p>
                <ul>
                  <li><strong>Jenis Tindakan</strong> - Deskripsi apresiasi</li>
                  <li><strong>Poin</strong> - Nilai poin (angka)</li>
                </ul>
                <p><strong>Catatan:</strong> Jika deskripsi sudah ada di database, hanya poin yang akan diperbarui.</p>
              </ImportFormatInfo>
            </FormGroup>

            <FormGroup>
              <Label>Pilih File Excel (.xlsx, .xls)</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
              />
            </FormGroup>

            {importData.length > 0 && (
              <FormGroup>
                <Label>Preview Data ({importData.length} baris)</Label>
                <PreviewTable>
                  <thead>
                    <tr>
                      <th>Baris</th>
                      <th>Jenis Tindakan</th>
                      <th>Poin</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importData.slice(0, 10).map((item, index) => (
                      <tr key={index} style={{ backgroundColor: item.valid ? '#f8fff8' : '#fff8f8' }}>
                        <td>{item.row}</td>
                        <td>{item.description}</td>
                        <td>{item.point}</td>
                        <td>
                          <StatusBadge $valid={item.valid}>
                            {item.valid ? '✓ Valid' : '✗ Error'}
                          </StatusBadge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </PreviewTable>
                {importData.length > 10 && (
                  <p style={{ textAlign: 'center', marginTop: '0.5rem', color: 'var(--text-light)' }}>
                    ... dan {importData.length - 10} baris lainnya
                  </p>
                )}
              </FormGroup>
            )}

            <ButtonGroup>
              <CancelButton onClick={() => setShowImportModal(false)}>
                Batal
              </CancelButton>
              <SubmitButton 
                onClick={handleImportSubmit} 
                disabled={importLoading || importData.length === 0}
              >
                {importLoading ? 'Mengimpor...' : `Import ${importData.filter(item => item.valid).length} Data`}
              </SubmitButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

export default AdminDaftarApresiasiPage;

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-light);
    margin: 0;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 2;
  @media (max-width: 1100px) {
    order: 1;
  }
`;

const ActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const SearchContainer = styled.div`
  
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const TableContainer = styled.div`
  background-color: var(--background);
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }

  th {
    background-color: var(--background-light);
    font-weight: 600;
  }

  tbody tr:hover {
    background-color: var(--background-light);
  }
`;

const ActionButton = styled.button`
  background-color: ${props => props.$danger ? 'var(--error)' : 'var(--primary)'};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  margin-right: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const AddButton = styled.button`
  background-color: #27ae60;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;

  &:hover {
    background-color: #219150;
    opacity: 0.95;
  }
`;

const ImportButton = styled.button`
  background-color: #3498db;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
  margin-right: 1rem;

  &:hover {
    background-color: #2980b9;
    opacity: 0.95;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--background);
  border-radius: 0.5rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  
  &:hover {
    color: var(--text-dark);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  background-color: #f5f6fa;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SubmitButton = styled(Button)`
  background-color: var(--primary);
  color: white;
  border: none;
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  border: 1px solid var(--border);
  color: var(--text);
`;

const DeleteButton = styled.button`
  background-color: #e74c3c !important;
  color: #fff !important;
  font-weight: bold;
  border: 2px solid #c0392b;
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.15);
  letter-spacing: 0.5px;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  margin-right: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: opacity 0.2s, background-color 0.2s;
  &:hover, &:focus {
    background-color: #c0392b !important;
    outline: 2px solid #e74c3c;
    color: #fff !important;
    opacity: 0.95;
  }
`;


// Notification Toast Styles
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

const ImportFormatInfo = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 0.5rem;
  
  p {
    margin: 0 0 0.5rem 0;
  }
  
  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    margin-bottom: 0.25rem;
  }
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  
  th, td {
    padding: 0.5rem;
    text-align: left;
    border: 1px solid #dee2e6;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
  }
  
  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => props.$valid ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$valid ? '#155724' : '#721c24'};
`;